import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookingDetails } from '../../types';
import { useGuestStay } from '../../hooks/useGuestStay';
import { formatCurrency } from '../../utils/currency';

interface HomeScreenProps {
  booking: BookingDetails;
  onLogout: () => void;
}

/**
 * Standard-layout home: hero with the stay summary, then the services as
 * tiles. Check-in is the primary action for arriving guests — and it says
 * up front when the hotel requires payment first ("₹X due before check-in"),
 * so nobody discovers the gate on the last step. Once checked in, food
 * ordering becomes the primary tile.
 */
const HomeScreen: React.FC<HomeScreenProps> = ({ booking, onLogout }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    hotelInfo, logoSrc, status, policy, checkinState, isCheckedIn, currency, canOrderFood,
  } = useGuestStay(booking);

  const hotelName = hotelInfo?.hotel?.name || booking.hotel?.name || '';
  const firstName =
    status?.guest_details?.first_name || (booking.guest_name || '').split(' ')[0];

  const fmtDate = (iso?: string) => {
    if (!iso) return '—';
    try {
      return new Date(iso + 'T00:00:00').toLocaleDateString(i18n.language, {
        weekday: 'short', day: 'numeric', month: 'short',
      });
    } catch {
      return iso;
    }
  };

  const dueNow = policy?.payment_required ? policy.amount_due_now : 0;
  const dueLabel = dueNow > 0 ? formatCurrency(dueNow, currency) : null;
  const balance = policy ? policy.balance_due : undefined;

  // Per-hotel feature config (admin "Guest App" page); absent field = on.
  const features = hotelInfo?.hotel?.features;
  const featureOn = (key: 'self_checkin' | 'payments' | 'housekeeping') =>
    features?.[key] !== false;
  // Unlike the others this defaults OFF — hotels opt in explicitly.
  const selfCheckoutOn = (features as any)?.self_checkout === true;

  // Stay finished — neither self-service flow applies any more.
  const isCheckedOut =
    checkinState === 'checked_out'
    || (status as any)?.reservation_status === 'checked_out'
    || (booking as any)?.status === 'checked_out';

  // Check-in opens on the arrival date. Check-out has no date gate:
  // once a guest is in-house they may leave early, and the
  // full-balance rule is what actually protects the hotel.
  const todayStr = new Date().toLocaleDateString('en-CA');
  const checkInStr = (status?.check_in_date || booking.check_in_date || '').slice(0, 10);
  const checkinDateReached = !checkInStr || todayStr >= checkInStr;
  // Per-hotel custom button names (admin Guest App page); empty = default.
  const buttonLabels = hotelInfo?.hotel?.button_labels || {};
  const btnLabel = (key: string, fallback: string) => buttonLabels[key] || fallback;
  const helpContact = hotelInfo?.hotel?.help_contact;
  const helpMode = helpContact?.mode || 'phone';
  const whatsappDigits = (helpContact?.whatsapp || '').replace(/[^0-9]/g, '');

  const checkinSubtitle = (() => {
    if (checkinState === 'pending_payment') {
      return t('gd.home.checkinPending', { amount: dueLabel });
    }
    if (dueLabel) {
      return t('gd.home.checkinDue', { amount: dueLabel });
    }
    if (checkinState === 'upcoming' && status?.can_checkin_from) {
      return t('gd.home.checkinOpens', { date: fmtDate(status.can_checkin_from) });
    }
    return t('gd.home.checkinFast');
  })();

  return (
    <section className="gd-screen">
      <div className="gd-hero">
        <div className="gd-hotel">
          <div className="gd-logo">
            {logoSrc ? <img src={logoSrc} alt={hotelName} /> : (hotelName || 'H').charAt(0)}
          </div>
          <div>
            <b>{hotelName}</b>
            <span>
              {[hotelInfo?.hotel?.city, hotelInfo?.hotel?.country].filter(Boolean).join(' · ')}
            </span>
          </div>
          {hotelInfo?.hotel?.phone && (
            <a className="gd-call" href={`tel:${hotelInfo.hotel.phone}`}>
              📞 {hotelInfo.hotel.phone}
            </a>
          )}
        </div>
        <h1>{firstName ? t('gd.home.welcomeName', { name: firstName }) : t('gd.home.welcome')}</h1>
        <p>{hotelInfo?.hotel?.welcome_text || t('gd.home.tagline')}</p>
        <div className="gd-stay">
          <div>
            <span>{t('gd.home.booking')}</span>
            <b>{booking.reference_number}</b>
          </div>
          <div>
            <span>{t('gd.home.room')}</span>
            <b>
              {booking.room_type}
              {booking.room_number ? ` · ${booking.room_number}` : ''}
            </b>
          </div>
          <div>
            <span>{t('gd.home.checkOut')}</span>
            <b>{fmtDate(status?.check_out_date || booking.check_out_date)}</b>
          </div>
        </div>
      </div>

      <div className="gd-grid">
        {featureOn('self_checkin') && !isCheckedOut && !isCheckedIn && checkinDateReached && checkinState === 'pending_payment' && (
          <button type="button" className="gd-tile gd-alert" onClick={() => navigate('/checkin')}>
            <div className="gd-ico">💳</div>
            <div>
              <b>{t('gd.home.paymentPendingTitle')}</b>
              <small>{checkinSubtitle}</small>
            </div>
          </button>
        )}
        {featureOn('self_checkin') && !isCheckedOut && !isCheckedIn && !checkinDateReached && (
          <div className="gd-tile" style={{ opacity: 0.65, cursor: 'default' }}>
            <div className="gd-ico">🗓️</div>
            <div>
              <b>{btnLabel('checkin', t('gd.home.checkinTitle'))}</b>
              <small>{t('gd.home.checkinOpens', { date: fmtDate(checkInStr) })}</small>
            </div>
          </div>
        )}
        {featureOn('self_checkin') && !isCheckedOut && !isCheckedIn && checkinDateReached && checkinState !== 'pending_payment' && (
          <button type="button" className="gd-tile gd-primary" onClick={() => navigate('/checkin')}>
            <div className="gd-ico">🪪</div>
            <div>
              <b>{btnLabel('checkin', t('gd.home.checkinTitle'))}</b>
              <small>{checkinSubtitle}</small>
            </div>
          </button>
        )}
        {selfCheckoutOn && isCheckedIn && !isCheckedOut && (
          <button type="button" className="gd-tile gd-primary" onClick={() => navigate('/checkout')}>
            <div className="gd-ico">🧳</div>
            <div>
              <b>{btnLabel('checkout', t('gd.home.checkoutTitle', 'Check out'))}</b>
              <small>{t('gd.home.checkoutSub', 'Settle up and check out from your phone')}</small>
            </div>
          </button>
        )}
        {canOrderFood && (
          <button
            type="button"
            className={`gd-tile ${isCheckedIn ? 'gd-primary' : 'gd-orange'}`}
            onClick={() => navigate('/food')}
          >
            <div className="gd-ico">🍽️</div>
            <div>
              <b>{btnLabel('food', t('gd.home.foodTitle'))}</b>
              <small>{t('gd.home.foodSub')}</small>
            </div>
          </button>
        )}
        {featureOn('housekeeping') && (
          <button type="button" className="gd-tile" onClick={() => navigate('/housekeeping')}>
            <div className="gd-ico">🧹</div>
            <div>
              <b>{btnLabel('housekeeping', t('gd.home.hkTitle'))}</b>
              <small>{isCheckedIn ? t('gd.home.hkSub') : t('gd.home.afterCheckin')}</small>
            </div>
          </button>
        )}
        {featureOn('payments') && (() => {
          const settled = !!policy && policy.grand_total > 0 && policy.balance_due <= 0;
          return (
            <button
              type="button"
              className={`gd-tile ${settled ? 'gd-paid' : 'gd-orange'}`}
              onClick={() => navigate('/pay')}
            >
              <div className="gd-ico">{settled ? '✅' : '💳'}</div>
              <div>
                <b>{btnLabel('pay', t('gd.home.payTitle'))}</b>
                <small>
                  {settled
                    ? t('gd.home.payPaid')
                    : balance !== undefined && balance > 0
                      ? t('gd.home.payBalance', { amount: formatCurrency(balance, currency) })
                      : t('gd.home.paySub')}
                </small>
              </div>
            </button>
          );
        })()}
        <button type="button" className="gd-tile gd-wide" onClick={() => navigate('/info')}>
          <div className="gd-ico">ℹ️</div>
          <div>
            <b>{btnLabel('info', t('gd.home.infoTitle'))}</b>
            <small>{t('gd.home.infoSub')}</small>
          </div>
        </button>
      </div>

      {helpMode !== 'hidden' && (
        helpMode === 'whatsapp' && whatsappDigits ? (
          <div className="gd-help">
            <div>
              <b>{t('gd.home.helpTitle')}</b>
              {t('gd.home.helpSub')}
            </div>
            <a href={`https://wa.me/${whatsappDigits}`} target="_blank" rel="noreferrer">
              WhatsApp
            </a>
          </div>
        ) : hotelInfo?.hotel?.phone ? (
          <div className="gd-help">
            <div>
              <b>{t('gd.home.helpTitle')}</b>
              {t('gd.home.helpSub')}
            </div>
            <a href={`tel:${hotelInfo.hotel.phone}`}>{t('gd.home.call')}</a>
          </div>
        ) : null
      )}

      <div className="gd-powered">
        {t('gd.home.poweredBy')} <b>FrontDesko</b>
        <div>
          <button
            type="button"
            onClick={onLogout}
            style={{
              font: 'inherit', background: 'none', border: 0, color: 'inherit',
              textDecoration: 'underline', cursor: 'pointer', marginTop: 6,
            }}
          >
            {t('gd.home.signOut')}
          </button>
        </div>
      </div>
    </section>
  );
};

export default HomeScreen;
